import { useState } from "react";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAppSelector } from "../../hooks/hooks";
import PaymentModal from "../../components/PaymentMethods/PaymentModal";
import { convertCurrencyAmount, formatCurrency } from "../../utils/utils";

type TransactionDataProps = {
  type: string;
  plan_id?: string;
  unique_number?: string;
  quantity?: string;
  additionalInputValue?: string;
  recipientLabel?: string;
  additionalInputLabel?: string;
  requiresRecipient?: boolean;
  requiresAdditionalInput?: boolean;
  onValidationError?: (error: { field: "unique_number" | "additional" | "none"; message: string | null }) => void;
};

const PaymentCard = (props: TransactionDataProps & { selectedPlan?: any }) => {
  const { darkMode } = useDarkMode();
  const [quantity, setQuantity] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentData] = useState(false);
  const { data: appData } = useAppSelector((state) => state.appData);
  const { selectedCurrency } = useAppSelector((state) => state.locale);

  const togglePaymentModal = () => {
    setShowPaymentModal(!showPaymentModal);
  };
  const handlePayNowClick = () => {
    const errors: string[] = [];
    const recipientRequired = Boolean(props.requiresRecipient);
    const additionalRequired = Boolean(props.requiresAdditionalInput);

    if (recipientRequired && !props.unique_number?.trim()) {
      props.onValidationError?.({
        field: "unique_number",
        message: `${props.recipientLabel || "Recipient"} is required.`,
      });
      return;
    }

    if (additionalRequired && !props.additionalInputValue?.trim()) {
      props.onValidationError?.({
        field: "additional",
        message: `${props.additionalInputLabel || "Additional details"} is required.`,
      });
      return;
    }

    props.onValidationError?.({ field: "none", message: null });
    togglePaymentModal();
  };

  function getOriginalPrice(discountedPrice: number, discountPercent: number) {
    const originalPrice = discountedPrice / (1 - discountPercent / 100);
    return originalPrice;
  }

  const currencies = appData.currencies || [];
  const getCurrencyById = (currencyId?: number) =>
    currencies.find((currency: any) => currency.id === currencyId);
  const dgCoinCurrency =
    currencies.find((currency: any) => currency.symbol === "DG") ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg coin"),
    );

  const formatConvertedPrice = (
    amount: number | string,
    currencyId?: number,
  ) => {
    const fromCurrency =
      getCurrencyById(currencyId) || selectedCurrency || currencies[0];
    const toCurrency = selectedCurrency || fromCurrency;
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      toCurrency,
    );
    return {
      symbol: toCurrency?.symbol || "",
      value: formatCurrency(converted),
    };
  };

  const formatDGCoinPrice = (amount: number | string, currencyId?: number) => {
    const fromCurrency = getCurrencyById(currencyId) || dgCoinCurrency;
    if (!fromCurrency || !dgCoinCurrency) {
      return formatCurrency(Number(amount));
    }
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      dgCoinCurrency,
    );
    return formatCurrency(converted);
  };

  return (
    <>
      {props.selectedPlan &&
        (() => {
          const { symbol, value } = formatConvertedPrice(
            props.selectedPlan.wallet_price,
            props.selectedPlan.price_currency_id,
          );
          const dgCoinValue = formatDGCoinPrice(
            props.selectedPlan.wallet_price,
            props.selectedPlan.price_currency_id,
          );
          const originalPrice =
            props.selectedPlan.discount > 0
              ? getOriginalPrice(
                  props.selectedPlan.wallet_price,
                  props.selectedPlan.discount,
                )
              : null;
          const convertedOriginal = originalPrice
            ? formatConvertedPrice(
                originalPrice,
                props.selectedPlan.price_currency_id,
              )
            : null;

          return (
            <div className={""}>
              <div className="flex flex-row items-center gap-3">
                <img
                  src={
                    props.selectedPlan.image_url
                      ? props.selectedPlan.image_url
                      : props.selectedPlan.provider?.image_url
                  }
                  alt="CP"
                 className="w-8 bp:w-20 aspect-square rounded-md object-cover flex-shrink-0"
                />
                <div className="flex flex-col w-full">
                  <div className="w-full bp:w-[350px] lg:w-[500px] flex items-center justify-between dark:bg-[#FFFFFF1A] gap-2 border border-border-border1 dark:border-tabs p-4 rounded-lg">
                    <div>
                      <p className="text-[10px] bp:text-base font-semibold font-lt text-black dark:text-white">
                        {props.selectedPlan.name}
                      </p>
                      <p className="text-[8px] bp:text-sm text-gray-500 font-lt dark:text-gray-400">
                        Product: {props.selectedPlan.category.provider.name}{" "}
                        {props.selectedPlan.category.name}
                      </p>

                      {props.selectedPlan.price_type === "fixed" && (
                        <div className="flex items-center gap-2">
                          <div className="bg-[#039EF2] text-white px-1 bp:px-1.5 bp:py-0.5 rounded-2xl flex items-center gap-1">
                            <div className="w-3 bp:w-4 h-3 bp:h-4 flex items-center justify-center">
                              <img
                                src="/png/Coins.png"
                                alt="Coins"
                                className="w-[14px] h-[14px]"
                              />
                            </div>
                            <p className="text-[8px] bp:text-sm font-semibold">
                              {dgCoinValue}
                            </p>
                          </div>
                          <span className="text-[10px] bp:text-base text-black dark:text-white">
                            {symbol} {value}{" "}
                            {convertedOriginal && (
                              <label className="text-gray-500 bp:text-xs text-[10px] line-through">
                                {convertedOriginal.symbol}
                                {convertedOriginal.value}
                              </label>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* <div className="flex gap-2 items-center">
                  <span
                    className="border border-border-border1 dark:border-tabs dark:text-white rounded p-1 w-4 h-4 bp:w-7 bp:h-7 flex items-center justify-center"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </span>
                  <span className="font-lt text-sm  bp:text-xl dark:text-white">
                    {quantity}
                  </span>
                  <span
                    className="border border-border-border1 dark:border-tabs dark:text-white rounded p-1 w-4 h-4 bp:w-7 bp:h-7 flex items-center justify-center"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </span>
                </div> */}

                    {props.selectedPlan.discount > 0 && (
                      <span className=" bg-[#FEF3F2] rounded-xl">
                        <p className="text-[#D92D20] text-xs font-semibold font-satoshi px-[2px] py-[2px]">
                          {formatCurrency(props.selectedPlan.discount)}% off
                        </p>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className=" flex justify-end gap-2 items-center w-full mt-4">
                {/* <button className="flex items-center gap-2 px-2 py-1 bp:px-4 bp:py-2 border rounded-lg text-black dark:text-white border-gray-300 dark:border-gray-600">
              <ShoppingBagIcon theme={darkMode ? "dark" : "light"} />
              <p className="text-[10px] bp:text-sm font-semibold">
                Add to Cart
              </p>
            </button> */}
                <button
                  className="px-4 py-2 text-[10px] bp:text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2]"
                  onClick={handlePayNowClick}
                >
                  Pay Now
                </button>
              </div>
              {/* Payment Modal */}
                <PaymentModal
                  isOpen={showPaymentModal}
                  togglePaymentModal={togglePaymentModal}
                  selectedPlan={props.selectedPlan}
                  unique_number={props.unique_number}
                  type={props.type}
                  additionalInputValue={props.additionalInputValue}
                />
            </div>
          );
        })()}
    </>
  );
};

export default PaymentCard;
